import { useState, useEffect, useCallback } from 'react';

const MOBILE_QUERY = '(max-width: 768px) and (pointer: coarse)';

function getForceOverride(): boolean | null {
    if (typeof window === 'undefined') return null;
    // Query param: ?mobile=true / ?mobile=false — persist to sessionStorage
    const params = new URLSearchParams(window.location.search);
    const paramVal = params.get('mobile');
    if (paramVal === 'true') { sessionStorage.setItem('forceMobile', 'true'); return true; }
    if (paramVal === 'false') { sessionStorage.removeItem('forceMobile'); return false; }
    // SessionStorage: survives HMR and React Router navigations
    if (sessionStorage.getItem('forceMobile') === 'true') return true;
    // LocalStorage: forceMobile = 'true' / 'false'
    const stored = localStorage.getItem('forceMobile');
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return null;
}

/**
 * Detects mobile context via viewport width + pointer type,
 * with dev overrides via ?mobile=true query param or
 * localStorage.setItem('forceMobile', 'true').
 */
export function useIsMobile(): boolean {
    const [isMobile, setIsMobile] = useState(() => {
        const override = getForceOverride();
        if (override !== null) return override;
        if (typeof window === 'undefined') return false;
        return window.matchMedia(MOBILE_QUERY).matches;
    });

    const update = useCallback(() => {
        const override = getForceOverride();
        if (override !== null) {
            setIsMobile(override);
            return;
        }
        setIsMobile(window.matchMedia(MOBILE_QUERY).matches);
    }, []);

    useEffect(() => {
        update();
        const mql = window.matchMedia(MOBILE_QUERY);
        const handler = () => update();
        mql.addEventListener('change', handler);
        // Also listen for storage changes (cross-tab forceMobile toggle)
        window.addEventListener('storage', handler);
        return () => {
            mql.removeEventListener('change', handler);
            window.removeEventListener('storage', handler);
        };
    }, [update]);

    return isMobile;
}
